import type { FirmContactView } from "../interfaces/models/IFirmContact";

export type FirmContactSingleField =
  | "primaryContact"
  | "secondaryContact"
  | "accountingContact"
  | "itContact"
  | "socialMediaContact"
  | "hrContact"
  | "ccContact";

export type FirmContactMultiField = "marketingContacts" | "liaisonContacts";

export type CoreFirmContactField =
  | "primaryContact"
  | "secondaryContact"
  | "accountingContact";

export const CORE_FIRM_CONTACT_FIELDS = new Set<CoreFirmContactField>([
  "primaryContact",
  "secondaryContact",
  "accountingContact",
]);

export function isCoreFirmContactField(
  field: string,
): field is CoreFirmContactField {
  return CORE_FIRM_CONTACT_FIELDS.has(field as CoreFirmContactField);
}

export type FirmContactSlot =
  | {
      key: string;
      field: CoreFirmContactField;
      title: string;
      contactType: string;
      cardinality: "single";
    }
  | {
      key: string;
      field: "itContact" | "socialMediaContact" | "hrContact" | "ccContact";
      title: string;
      contactType: string;
      cardinality: "single";
      optional: true;
    }
  | {
      key: string;
      field: FirmContactMultiField;
      title: string;
      contactType: string;
      cardinality: "multi";
      maxSelections: number;
    };

/** Canonical firm-contact roles used across Manage Firm and Add Professional. */
export const FIRM_CONTACT_SLOTS: FirmContactSlot[] = [
  {
    key: "primary",
    field: "primaryContact",
    title: "Primary Contact",
    contactType: "primaryContact",
    cardinality: "single",
  },
  {
    key: "secondary",
    field: "secondaryContact",
    title: "Secondary Contact",
    contactType: "secondaryContact",
    cardinality: "single",
  },
  {
    key: "accounting",
    field: "accountingContact",
    title: "Accounting Contact",
    contactType: "accountingContact",
    cardinality: "single",
  },
  {
    key: "ccContact",
    field: "ccContact",
    title: "CC - Contact",
    contactType: "ccContact",
    cardinality: "single",
    optional: true,
  },
  {
    key: "it",
    field: "itContact",
    title: "IT Contact",
    contactType: "itContact",
    cardinality: "single",
    optional: true,
  },
  {
    key: "social",
    field: "socialMediaContact",
    title: "Social Media Contact",
    contactType: "socialMediaContact",
    cardinality: "single",
    optional: true,
  },
  {
    key: "hr",
    field: "hrContact",
    title: "HR Contact",
    contactType: "hrContact",
    cardinality: "single",
    optional: true,
  },
  {
    key: "marketing",
    field: "marketingContacts",
    title: "Marketing Contact",
    contactType: "marketingContact",
    cardinality: "multi",
    maxSelections: 5,
  },
  {
    key: "liaison",
    field: "liaisonContacts",
    title: "Liaison Contact",
    contactType: "liaisonContact",
    cardinality: "multi",
    maxSelections: 5,
  },
];

/** Manage Firm → Professionals: CC contact is managed in Accounting, not here. */
export const FIRM_CONTACT_SLOTS_MANAGE_PROFESSIONALS: FirmContactSlot[] =
  FIRM_CONTACT_SLOTS.filter((slot) => slot.field !== "ccContact");

type ContactsData = FirmContactView["primaryContact"];

export function isFirmContactAssigned(
  contact: ContactsData | undefined,
): boolean {
  if (!contact) {
    return false;
  }
  const name = contact.fullName?.trim() ?? "";
  const email = contact.email?.trim() ?? "";
  const phone = contact.phone?.phone?.trim() ?? "";
  return Boolean(String(contact.id ?? "").trim() || name || email || phone);
}

export function firmContactDisplayName(
  contact: ContactsData | undefined,
): string {
  if (!isFirmContactAssigned(contact)) {
    return "None";
  }
  return contact!.fullName?.trim() || contact!.email?.trim() || "Assigned";
}

export function getAssignedMultiContacts(
  contacts: ContactsData[] | undefined,
): ContactsData[] {
  return (contacts ?? []).filter(isFirmContactAssigned);
}
